import { useState } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, Loader2, Building2, MapPin, Ruler, User } from 'lucide-react';
import { sk } from '@/utils/stableKey';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { label: 'Property', icon: MapPin },
  { label: 'Interest', icon: Building2 },
  { label: 'Details', icon: User },
];

const interests = [
  { value: 'sell_developer', label: 'Sell to a developer', desc: 'Get a premium price from qualified developers' },
  { value: 'subdivide', label: 'Subdivide my block', desc: 'Split into two or more lots' },
  { value: 'build_units', label: 'Build units/townhouses', desc: 'Develop the property myself' },
  { value: 'not_sure', label: 'Not sure yet', desc: 'I want expert advice on my options' },
];

const DevelopmentEnquiryTool = ({ source = '', suburb = '' }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    address: '', suburb: suburb, block_size: '', current_use: 'house',
    interest: '', first_name: '', last_name: '', email: '', phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const canNext = () => {
    if (step === 0) return form.address.trim().length > 0;
    if (step === 1) return form.interest.length > 0;
    if (step === 2) return form.first_name.trim() && form.last_name.trim() && form.email.trim();
    return false;
  };

  const handleSubmit = async () => {
    if (!canNext()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/forms/development-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          name: `${form.first_name} ${form.last_name}`.trim(),
          source,
        }),
      });
      if (res.ok) setSubmitted(true);
      else setError('Something went wrong. Please try again.');
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="my-12 bg-[#042E0C] rounded-2xl p-8 sm:p-10 text-center" data-testid="dev-enquiry-success">
        <CheckCircle size={48} className="mx-auto text-[#C4A36B] mb-4" />
        <h3 className="text-2xl font-heading font-light text-white mb-2">Assessment Requested</h3>
        <p className="text-white/60 text-sm max-w-md mx-auto">
          Our development advisory team will prepare a personalised assessment of your property and contact you within 48 hours.
        </p>
      </div>
    );
  }

  return (
    <div className="my-12 bg-gradient-to-br from-[#042E0C] to-[#0a4a15] rounded-2xl overflow-hidden" data-testid="development-enquiry-tool">
      {/* Step indicators */}
      <div className="flex border-b border-white/10">
        {steps.map((s, i) => (
          <div
            key={sk(s, i)}
            className={`flex-1 flex items-center justify-center gap-2 py-4 text-xs font-medium tracking-wider uppercase transition-colors ${
              i === step ? 'text-[#C4A36B] bg-white/5' : i < step ? 'text-white/50' : 'text-white/20'
            }`}
          >
            <s.icon size={14} />
            <span className="hidden sm:inline">{s.label}</span>
            {i < step && <CheckCircle size={12} className="text-[#C4A36B]" />}
          </div>
        ))}
      </div>

      <div className="p-8 sm:p-10">
        <h3 className="font-heading text-xl sm:text-2xl font-light text-white mb-1">
          {step === 0 && 'What property do you want assessed?'}
          {step === 1 && 'What are you interested in?'}
          {step === 2 && 'How should we contact you?'}
        </h3>
        <p className="text-white/40 text-sm mb-6">
          {step === 0 && 'Our team will review your zoning, block size, and development potential.'}
          {step === 1 && 'This helps us tailor your assessment to your goals.'}
          {step === 2 && 'We\'ll send your personalised assessment within 48 hours.'}
        </p>

        {/* Step 0: Property */}
        {step === 0 && (
          <div className="space-y-4">
            <input
              data-testid="dev-enquiry-address"
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="Property address *"
              className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input
                data-testid="dev-enquiry-suburb"
                value={form.suburb}
                onChange={(e) => update('suburb', e.target.value)}
                placeholder="Suburb"
                className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
              />
              <div className="relative">
                <Ruler size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  data-testid="dev-enquiry-blocksize"
                  value={form.block_size}
                  onChange={(e) => update('block_size', e.target.value)}
                  placeholder="Block size (m2)"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
                />
              </div>
              <select
                data-testid="dev-enquiry-currentuse"
                value={form.current_use}
                onChange={(e) => update('current_use', e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm appearance-none"
              >
                <option value="house" className="bg-[#042E0C]">House</option>
                <option value="duplex" className="bg-[#042E0C]">Duplex</option>
                <option value="unit" className="bg-[#042E0C]">Unit/Apartment</option>
                <option value="vacant" className="bg-[#042E0C]">Vacant Land</option>
                <option value="commercial" className="bg-[#042E0C]">Commercial</option>
                <option value="other" className="bg-[#042E0C]">Other</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 1: Interest */}
        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {interests.map((opt) => (
              <button
                key={opt.value}
                data-testid={`dev-enquiry-interest-${opt.value}`}
                onClick={() => update('interest', opt.value)}
                className={`text-left p-4 rounded-lg border transition-all ${
                  form.interest === opt.value
                    ? 'border-[#C4A36B] bg-[#C4A36B]/10'
                    : 'border-white/10 hover:border-white/25 bg-white/5'
                }`}
              >
                <div className={`text-sm font-medium mb-1 ${form.interest === opt.value ? 'text-[#C4A36B]' : 'text-white'}`}>
                  {opt.label}
                </div>
                <div className="text-xs text-white/40">{opt.desc}</div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Contact */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                data-testid="dev-enquiry-first-name"
                value={form.first_name}
                onChange={(e) => update('first_name', e.target.value)}
                placeholder="First name *"
                className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
              />
              <input
                data-testid="dev-enquiry-last-name"
                value={form.last_name}
                onChange={(e) => update('last_name', e.target.value)}
                placeholder="Last name *"
                className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                data-testid="dev-enquiry-email"
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="Email *"
                className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
              />
              <input
                data-testid="dev-enquiry-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="Phone"
                className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
              />
            </div>
          </div>
        )}

        {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          {step > 0 ? (
            <button
              onClick={() => setStep(s => s - 1)}
              className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors"
              data-testid="dev-enquiry-back"
            >
              <ArrowLeft size={16} /> Back
            </button>
          ) : <div />}

          {step < 2 ? (
            <button
              onClick={() => canNext() && setStep(s => s + 1)}
              disabled={!canNext()}
              className="flex items-center gap-2 px-6 py-3 bg-[#C4A36B] text-[#042E0C] rounded-lg font-medium text-sm hover:bg-[#C4A36B]/90 transition-colors disabled:opacity-40"
              data-testid="dev-enquiry-next"
            >
              Next <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canNext() || submitting}
              className="flex items-center gap-2 px-6 py-3 bg-[#C4A36B] text-[#042E0C] rounded-lg font-medium text-sm hover:bg-[#C4A36B]/90 transition-colors disabled:opacity-40"
              data-testid="dev-enquiry-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Get My Assessment <ArrowRight size={16} /></>}
            </button>
          )}
        </div>

        <p className="text-white/20 text-xs mt-4 text-center">100% free. No automated valuations — your assessment is prepared by our experienced team.</p>
      </div>
    </div>
  );
};

export default DevelopmentEnquiryTool;
